import { Eye } from 'lucide-react';
import { t } from '../../i18n/index.js';
import { useOthersInProject, useViewers } from '../../platform/presence.js';
import { initials } from './Pickers.js';

/**
 * Präsenz (Paket P9, Konzept „Wer ist da“): Avatare im Projektkopf und ein Hinweis, wer dieselbe Aufgabe offen hat.
 * Erscheint nur, wenn jemand anderes da ist (Regel R2).
 */
export function PresenceAvatars({ projectId }: { projectId: string }) {
  const others = useOthersInProject(projectId);
  if (!others.length) return null;
  const names = others.map((p) => p.name).join(', ');
  return (
    <span className="flex items-center -space-x-2 px-1" role="group" aria-label={t('presence.here', { names })} title={t('presence.here', { names })}>
      {others.slice(0, 3).map((p) => (
        <span key={p.userId} className="grid size-7 place-items-center rounded-full border-2 border-white bg-[var(--color-accent)] text-[10px] font-semibold text-on-accent dark:border-zinc-950">
          {initials(p.name)}
        </span>
      ))}
      {others.length > 3 && <span className="pl-2 text-xs text-zinc-500">+{others.length - 3}</span>}
    </span>
  );
}

/** Kleines Auge an Zeile/Karte: jemand anderes hat diese Aufgabe gerade offen */
export function ViewersMark({ projectId, taskId }: { projectId: string; taskId: string }) {
  const names = useViewers(projectId, taskId);
  if (!names) return null;
  const label = t('presence.viewing', { names: names.split('\n').join(', ') });
  return (
    <span aria-label={label} title={label} className="flex items-center px-1 text-[var(--color-accent)]">
      <Eye className="size-3.5" aria-hidden />
    </span>
  );
}

/** In den Details: „Mia hat diese Aufgabe auch offen“ */
export function ViewersNotice({ projectId, taskId }: { projectId: string; taskId: string }) {
  const names = useViewers(projectId, taskId);
  if (!names) return null;
  return (
    <p role="status" className="flex items-center gap-2 rounded-lg bg-zinc-100 px-3 py-2 text-sm dark:bg-zinc-800">
      <Eye className="size-4 text-[var(--color-accent)]" aria-hidden />
      {t('presence.alsoOpen', { names: names.split('\n').join(', ') })}
    </p>
  );
}
